import {
  ArrowLeftRight,
  Brush,
  Copy,
  Dices,
  FlipHorizontal2,
  Layers,
  ListOrdered,
  Plus,
  RotateCcw,
  RotateCw,
  Shuffle,
  Trash2,
  Waves,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { patternBeats } from '../audio/sequencer';
import { DIVISIONS } from '../audio/slice';
import { useBeat } from '../lib/hooks';
import { makeLinearPattern } from '../store/defaults';
import {
  patternOf,
  sourceOf,
  unusedPatternIds,
  useStore,
  type TransformOp,
} from '../store/useStore';
import type { Division } from '../types';
import { OrderField } from './OrderField';
import { PatternGrid } from './PatternGrid';
import { MixToggle, PRESET_MENUS, PresetMenu, presetCount, StraightButton } from './PresetPicker';
import { Popover } from './ui/Popover';
import { StepInspector } from './StepInspector';
import { Field, NumberField, Select } from './ui/Controls';

const TRANSFORMS: { op: TransformOp; label: string; icon: JSX.Element; title: string }[] = [
  { op: 'reverse-order', label: 'Flip', icon: <ArrowLeftRight size={12} />, title: 'Play the steps back to front' },
  { op: 'rotate-left', label: '', icon: <RotateCcw size={12} />, title: 'Rotate pattern left' },
  { op: 'rotate-right', label: '', icon: <RotateCw size={12} />, title: 'Rotate pattern right' },
  { op: 'shuffle', label: 'Shuffle', icon: <Shuffle size={12} />, title: 'Randomly reorder the steps' },
  { op: 'reverse-all', label: 'Rev all', icon: <FlipHorizontal2 size={12} />, title: 'Toggle reverse on every step' },
  { op: 'reverse-alt', label: 'Rev alt', icon: <FlipHorizontal2 size={12} />, title: 'Toggle reverse on every other step' },
  { op: 'random-slices', label: 'Reslice', icon: <Dices size={12} />, title: 'Assign random slices' },
  { op: 'humanize', label: 'Human', icon: <Waves size={12} />, title: 'Add subtle timing and level variation' },
  { op: 'stutter-last', label: 'Stutter', icon: <Zap size={12} />, title: 'Retrigger the final two steps' },
  { op: 'thin', label: 'Thin', icon: <Layers size={12} />, title: 'Mute a scattering of off-beats' },
  { op: 'double', label: '×2', icon: <></>, title: 'Double time — same material, half the space' },
  { op: 'half', label: '÷2', icon: <></>, title: 'Half time — insert a rest after every step' },
  { op: 'linear', label: '1:1', icon: <></>, title: 'Reset to straight 1:1 slice order' },
];

/** Pattern workspace for the selected track. */
export function SequenceView() {
  const project = useStore((s) => s.project);
  const selectedTrackId = useStore((s) => s.selectedTrackId);
  const selectedSteps = useStore((s) => s.selectedSteps);
  const setUi = useStore((s) => s.setUi);
  const store = useStore();
  const beat = useBeat();
  /** Stack presets rather than rebuilding from straight each time. */
  const [mix, setMix] = useState(false);

  const track = project.tracks.find((t) => t.id === selectedTrackId) ?? project.tracks[0] ?? null;
  const pattern = patternOf(project, track);
  const source = sourceOf(project, track);
  const unused = unusedPatternIds(project);

  if (!track) return null;

  if (!pattern) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <p className="max-w-xs text-center text-xs leading-relaxed text-ink-400">
          {track.name} has no pattern yet. Create one — it will chop {source?.name ?? 'the loop'} into
          steps you can rearrange.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => {
            const pat = makeLinearPattern(source?.name ?? 'Pattern', 16, 4, track.color);
            store.addPattern(pat);
            store.assignPattern(track.id, pat.id);
          }}
        >
          <Plus size={13} /> New pattern
        </button>
      </div>
    );
  }

  const pBeats = patternBeats(pattern);
  const currentStep =
    store.playing && pBeats > 0
      ? Math.floor(((beat % pBeats) + pBeats) % pBeats * pattern.division) % pattern.length
      : -1;

  const applyPreset = (preset: { id: string; name: string }) => {
    store.applyPreset(pattern.id, preset.id, mix);
    setUi({ selectedSteps: [] });
    store.notify(mix ? `+ ${preset.name}` : preset.name);
  };

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Pattern">
            <div className="flex items-center gap-1">
              <Select
                className="w-44"
                value={pattern.id}
                options={project.patterns.map((p) => ({ value: p.id, label: p.name }))}
                onChange={(id) => store.assignPattern(track.id, id)}
              />
              <button
                className="btn btn-icon"
                title="New pattern"
                onClick={() => {
                  const pat = makeLinearPattern(
                    source ? `${source.name} ${project.patterns.length + 1}` : 'Pattern',
                    pattern.length,
                    pattern.division,
                    track.color,
                  );
                  store.addPattern(pat);
                  store.assignPattern(track.id, pat.id);
                }}
              >
                <Plus size={12} />
              </button>
              <button
                className="btn btn-icon"
                title="Duplicate pattern"
                onClick={() => {
                  const id = store.duplicatePattern(pattern.id);
                  if (id) store.assignPattern(track.id, id);
                }}
              >
                <Copy size={12} />
              </button>
              <button
                className="btn btn-icon"
                title="Delete pattern"
                onClick={() => store.removePattern(pattern.id)}
              >
                <Trash2 size={12} />
              </button>
              <button
                className="btn btn-icon"
                disabled={!unused.length}
                title={
                  unused.length
                    ? `Remove ${unused.length} pattern${unused.length > 1 ? 's' : ''} no track or scene is using`
                    : 'No unused patterns'
                }
                onClick={() => {
                  const n = unused.length;
                  store.removeUnusedPatterns();
                  store.notify(`Removed ${n} unused pattern${n > 1 ? 's' : ''}`);
                }}
              >
                <Brush size={12} />
                {unused.length > 0 && <span className="mono text-[9px]">{unused.length}</span>}
              </button>
            </div>
          </Field>

          <Field label="Name">
            <input
              className="field w-36"
              value={pattern.name}
              onChange={(e) => store.updatePattern(pattern.id, { name: e.target.value })}
            />
          </Field>

          <Field label="Steps">
            <NumberField
              value={pattern.length}
              min={1}
              max={256}
              width="w-16"
              onChange={(v) => store.setPatternLength(pattern.id, v)}
            />
          </Field>

          <Field label="Grid">
            <Select<string>
              className="w-28"
              value={String(pattern.division)}
              options={DIVISIONS.map((d) => ({ value: String(d.value), label: d.label }))}
              onChange={(v) =>
                store.updatePattern(pattern.id, { division: Number(v) as Division })
              }
            />
          </Field>

          <Field label={`Swing ${Math.round(pattern.swing * 100)}%`}>
            <input
              type="range"
              className="w-28"
              min={0}
              max={0.75}
              step={0.01}
              value={pattern.swing}
              onChange={(e) =>
                store.updatePattern(pattern.id, { swing: parseFloat(e.target.value) })
              }
            />
          </Field>

          <div className="ml-auto flex items-end gap-1">
            <Field label="Copy pattern to">
              <div className="flex gap-1">
                {project.tracks
                  .filter((t) => t.id !== track.id)
                  .map((t) => (
                    <button
                      key={t.id}
                      className="btn btn-icon"
                      title={`Apply "${pattern.name}" to ${t.name}`}
                      style={{ color: t.color, borderColor: `${t.color}55` }}
                      onClick={() => {
                        store.assignPattern(t.id, pattern.id);
                        store.notify(`${pattern.name} → ${t.name}`);
                      }}
                    >
                      {t.name.replace(/[^0-9]/g, '') || t.name.slice(0, 2)}
                    </button>
                  ))}
              </div>
            </Field>
          </div>
        </div>

        {/* One toolbar row. Presets, shaping and the typed order all live in
            palettes that stay open while you work, rather than three permanent
            rows of controls competing with the grid. */}
        <div className="flex flex-wrap items-center gap-1 border-y border-ink-700 py-2">
          <StraightButton color={track.color} onApply={applyPreset} />

          <MixToggle mix={mix} onChange={setMix} />

          <span className="mx-0.5 h-5 w-px bg-ink-700" />

          {/* One button per kind of move. Presets are the point of this screen,
              so they get the toolbar rather than a single catch-all menu. */}
          {PRESET_MENUS.map((group, i) => (
            <Popover
              key={group}
              anchorId={i === 0 ? 'presets' : undefined}
              label={group}
              badge={<span className="mono text-[9px] text-ink-400">{presetCount(group)}</span>}
              width="w-[34rem]"
            >
              <PresetMenu group={group} onApply={applyPreset} />
            </Popover>
          ))}

          <span className="mx-0.5 h-5 w-px bg-ink-700" />

          <Popover label="Shape" icon={<Shuffle size={12} />} width="w-[30rem]">
            <div className="flex flex-wrap gap-1">
              {TRANSFORMS.map((t) => (
                <button
                  key={t.op + t.label}
                  className="btn"
                  title={t.title}
                  onClick={() => store.transform(pattern.id, t.op)}
                >
                  {t.icon}
                  {t.label || t.title.split(' ')[0]}
                </button>
              ))}
            </div>
          </Popover>

          <Popover label="Order" icon={<ListOrdered size={12} />} width="w-[38rem]">
            <OrderField
              pattern={pattern}
              onApply={(slices) => store.setPatternOrder(pattern.id, slices)}
            />
          </Popover>

        </div>

        {!source && (
          <p className="rounded-lg border border-amber-hot/30 bg-amber-hot/10 px-3 py-2 text-[11px] text-amber-hot">
            {track.name} has a pattern but no loop. Pick one in the track strip below — the pattern
            will re-chop whatever you point it at.
          </p>
        )}

        <PatternGrid
          pattern={pattern}
          source={source}
          color={track.color}
          currentStep={currentStep}
          selected={selectedSteps}
          beatsPerBar={project.beatsPerBar}
          onSelect={(ids) => setUi({ selectedSteps: ids })}
          onMove={(from, to) => store.moveStep(pattern.id, from, to)}
          onStepChange={(id, p) => store.updateStep(pattern.id, id, p)}
        />
      </div>

      <div className="w-56 shrink-0 border-l border-ink-700 bg-ink-900">
        <StepInspector pattern={pattern} source={source} color={track.color} />
      </div>
    </div>
  );
}
